import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import * as Dialog from '@radix-ui/react-dialog'
import type { OrderDetailResponse, VisitRow } from '@fitting-room/contracts'
import { Unlink } from 'lucide-react'
import { useAppointmentTypes, useAppointments, useLinkOrderAppointment, useOrders, useUnlinkOrderAppointment } from '../../../lib/adminQueries'
import { appointmentHref } from '../../../lib/appointments'
import { BOUTIQUE_LOCALE, boutiqueDayOf, boutiqueTime, boutiqueToday } from '../../../lib/boutique'
import { formatDay } from '../../../lib/format'
import { shiftDay } from '../../../lib/months'
import { formatCents } from '../../../lib/orders'
import { useStaffToken } from '../../../lib/staffToken'
import { VisitPill } from '../StatusPill'
import { OrderStatusBadge } from './OrderStatusBadge'

/** How far either side of today the link search looks: a year back for the first fitting, a year ahead for the pickup. */
const SEARCH_SPAN_DAYS = 365

function whenOf(visit: Pick<VisitRow, 'scheduledAt'>): string {
  return `${formatDay(boutiqueDayOf(visit.scheduledAt), BOUTIQUE_LOCALE)} · ${boutiqueTime(visit.scheduledAt)}`
}

/**
 * The appointments on an order: the one it was written at, the fittings, the
 * pickup. tperent's `RelatedAppointments`, less booking a pickup from here —
 * that arrives with pickups. Unlinking leaves the appointment exactly as it
 * was; it just no longer belongs to this order.
 */
export function OrderAppointments({ detail }: { readonly detail: OrderDetailResponse }) {
  const { token } = useStaffToken()
  const types = useAppointmentTypes(token).data?.types
  const editable = detail.order.status === 'open'
  const [linking, setLinking] = useState(false)
  const [unlinking, setUnlinking] = useState<VisitRow | null>(null)
  const unlink = useUnlinkOrderAppointment(token, detail.order.id)

  return (
    <section className="st-card px-4 py-3" aria-labelledby="order-appointments">
      <h2 id="order-appointments" className="st-eyebrow mb-2">
        Appointments
      </h2>
      {detail.appointments.length === 0 ? (
        <p className="st-meta text-sm">Not linked to any appointment.</p>
      ) : (
        <ul className="divide-y divide-[color:var(--st-rule)]">
          {detail.appointments.map((visit) => (
            <li key={visit.id} className="flex items-center gap-2 py-2" data-visit-id={visit.id}>
              <Link to={appointmentHref(visit)} className="min-w-0 flex-1 text-sm hover:underline">
                <span className="tabular-nums">{whenOf(visit)}</span>
                <span className="st-meta block text-xs">{types?.find((t) => t.key === visit.tierKey)?.label ?? visit.tierKey}</span>
              </Link>
              <VisitPill status={visit.status} />
              {editable ? (
                <button type="button" className="st-iconbtn" aria-label={`Unlink the appointment on ${whenOf(visit)}`} onClick={() => setUnlinking(visit)}>
                  <Unlink size={14} aria-hidden />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editable ? (
        <button type="button" className="st-btn-quiet mt-2" onClick={() => setLinking(true)}>
          Link an appointment
        </button>
      ) : null}

      <LinkAppointmentDialog detail={detail} open={linking} onClose={() => setLinking(false)} />

      <Dialog.Root
        open={unlinking !== null}
        onOpenChange={(next) => {
          if (!next) {
            unlink.reset()
            setUnlinking(null)
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="st-overlay" />
          <Dialog.Content className="st-dialog" aria-describedby={undefined}>
            {unlinking ? (
              <>
                <Dialog.Title className="st-display mb-2 text-lg">Unlink the appointment on {whenOf(unlinking)}?</Dialog.Title>
                <p className="st-meta mb-4 text-sm">The appointment isn&rsquo;t canceled or moved. It just won&rsquo;t belong to this order anymore.</p>
                {unlink.isError ? (
                  <p className="st-alert mb-3" role="alert">
                    {unlink.error.message}
                  </p>
                ) : null}
                <div className="flex justify-end gap-2">
                  <Dialog.Close asChild>
                    <button type="button" className="st-btn-ghost">
                      Not now
                    </button>
                  </Dialog.Close>
                  <button
                    type="button"
                    className="st-btn"
                    disabled={unlink.isPending}
                    onClick={() => unlink.mutate(unlinking.id, { onSuccess: () => setUnlinking(null) })}
                  >
                    Unlink
                  </button>
                </div>
              </>
            ) : null}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  )
}

/** Typed into a search box, settled for a beat before it is a request. */
function useSettled(value: string, ms = 250): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}

/**
 * An existing appointment onto this order. Searched by phone first, as
 * tperent's is — a name on a booking is often just a first name, a number
 * rarely lies.
 */
function LinkAppointmentDialog({ detail, open, onClose }: { readonly detail: OrderDetailResponse; readonly open: boolean; readonly onClose: () => void }) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="st-overlay" />
        <Dialog.Content className="st-dialog" aria-describedby={undefined}>
          {open ? <LinkAppointmentForm detail={detail} onClose={onClose} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function LinkAppointmentForm({ detail, onClose }: { readonly detail: OrderDetailResponse; readonly onClose: () => void }) {
  const { token } = useStaffToken()
  const today = boutiqueToday()
  const [q, setQ] = useState(detail.order.customerPhone ?? detail.order.customerName)
  const settled = useSettled(q.trim())
  const results = useAppointments(
    settled ? token : null,
    { from: shiftDay(today, -SEARCH_SPAN_DAYS), to: shiftDay(today, SEARCH_SPAN_DAYS) },
    { q: settled },
  )
  const link = useLinkOrderAppointment(token)
  const visits = (results.data?.visits ?? []).filter((visit) => !visit.orders.some((ref) => ref.id === detail.order.id))

  return (
    <>
      <Dialog.Title className="st-display mb-3 text-lg">Link an appointment</Dialog.Title>
      <label className="flex flex-col gap-1">
        <span className="st-eyebrow">Customer name or phone</span>
        <input className="st-input" value={q} autoFocus onChange={(event) => setQ(event.target.value)} />
      </label>
      <div className="mt-3 max-h-72 overflow-y-auto">
        {settled && results.isPending ? <p className="st-meta text-sm">Searching…</p> : null}
        {results.isSuccess && visits.length === 0 ? <p className="st-meta text-sm">No appointment to link.</p> : null}
        <ul className="space-y-1.5">
          {visits.map((visit) => (
            <li key={visit.id}>
              <button
                type="button"
                className="st-pick"
                disabled={link.isPending}
                onClick={() => link.mutate({ orderId: detail.order.id, visitId: visit.id }, { onSuccess: onClose })}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium tabular-nums">{whenOf(visit)}</span>
                  <span className="st-meta block truncate text-xs">
                    {visit.customerName ?? 'No name'}
                    {visit.customerPhone ? ` · ${visit.customerPhone}` : ''}
                    {visit.orders.length > 0 ? ` · on ${visit.orders.map((ref) => ref.orderNo).join(', ')}` : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {link.isError ? (
        <p className="st-alert mt-3" role="alert">
          {link.error.message}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end">
        <button type="button" className="st-btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </>
  )
}

/**
 * From an appointment's drawer: put this appointment on an order already
 * written — a second fitting, the pickup. tperent's `LinkToOrderDialog`:
 * searched by phone first, and only orders still open are offered, because
 * only those can take it.
 */
export function LinkToOrderDialog({ visit, open, onClose }: { readonly visit: VisitRow; readonly open: boolean; readonly onClose: () => void }) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="st-overlay" />
        <Dialog.Content className="st-dialog" aria-describedby={undefined}>
          {open ? <LinkToOrderForm visit={visit} onClose={onClose} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function LinkToOrderForm({ visit, onClose }: { readonly visit: VisitRow; readonly onClose: () => void }) {
  const { token } = useStaffToken()
  const [q, setQ] = useState(visit.customerPhone ?? visit.customerName ?? '')
  const settled = useSettled(q.trim())
  const results = useOrders(settled ? token : null, { q: settled, statuses: ['open'], limit: 10 })
  const link = useLinkOrderAppointment(token)
  const orders = settled ? (results.data?.orders ?? []) : []
  return (
    <>
      <Dialog.Title className="st-display mb-3 text-lg">Link to an order</Dialog.Title>
      <label className="flex flex-col gap-1">
        <span className="st-eyebrow">Order number, name or phone</span>
        <input className="st-input" value={q} autoFocus onChange={(event) => setQ(event.target.value)} />
      </label>
      <div className="mt-3 max-h-72 overflow-y-auto">
        {settled && results.isPending ? <p className="st-meta text-sm">Searching…</p> : null}
        {settled && results.isSuccess && orders.length === 0 ? <p className="st-meta text-sm">No open order matches.</p> : null}
        <ul className="space-y-1.5">
          {orders.map((order) => (
            <li key={order.id}>
              <button
                type="button"
                className="st-pick"
                disabled={link.isPending}
                onClick={() => link.mutate({ orderId: order.id, visitId: visit.id }, { onSuccess: onClose })}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium tabular-nums">
                    {order.orderNo} · {order.customerName}
                  </span>
                  <span className="st-meta block truncate text-xs">
                    {order.summary || 'Nothing on it yet'} · {formatCents(order.totalMinor)}
                  </span>
                </span>
                <OrderStatusBadge status={order.status} />
              </button>
            </li>
          ))}
        </ul>
      </div>
      {link.isError ? (
        <p className="st-alert mt-3" role="alert">
          {link.error.message}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end">
        <button type="button" className="st-btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </>
  )
}
